import {
  Artwork,
  Language,
  SUPPORTED_LANGUAGES,
} from '../types';

const LANGUAGE_SHORT_CODES: Record<Language, string> = {
  english: 'en',
  hindi: 'hi',
  kannada: 'kn',
};

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function languageOrder(preferred: Language): Language[] {
  return [
    preferred,
    ...SUPPORTED_LANGUAGES.filter((lang) => lang !== preferred),
  ];
}

function readTranslationField(
  artwork: Artwork,
  lang: Language,
  field: 'title' | 'artist' | 'description'
): string | undefined {
  const translation = artwork.translations?.[lang]?.[field];
  if (isNonEmptyString(translation)) {
    return translation.trim();
  }

  return undefined;
}

function readLocalizedField(
  artwork: Artwork,
  lang: Language,
  field: 'title' | 'artist' | 'description'
): string | undefined {
  for (const candidate of languageOrder(lang)) {
    const translated = readTranslationField(artwork, candidate, field);
    if (translated) {
      return translated;
    }
  }

  const fallback = artwork[field];
  return isNonEmptyString(fallback) ? fallback.trim() : undefined;
}

function readAudioFromMap(
  map: Partial<Record<string, string | null | undefined>> | undefined,
  lang: Language
): string | undefined {
  if (!map) {
    return undefined;
  }

  const fullKey = map[lang];
  if (isNonEmptyString(fullKey)) {
    return fullKey.trim();
  }

  const shortKey = LANGUAGE_SHORT_CODES[lang];
  const shortValue = map[shortKey];
  if (isNonEmptyString(shortValue)) {
    return shortValue.trim();
  }

  return undefined;
}

export function getArtworkTitle(
  artwork: Artwork,
  lang: Language
): string | undefined {
  return readLocalizedField(artwork, lang, 'title');
}

export function getArtworkArtist(
  artwork: Artwork,
  lang: Language
): string | undefined {
  return readLocalizedField(artwork, lang, 'artist');
}

export function getArtworkDescription(
  artwork: Artwork,
  lang: Language
): string | undefined {
  return readLocalizedField(artwork, lang, 'description');
}

export function getArtworkAudioUrl(
  artwork: Artwork,
  lang: Language
): string | null {
  const audioMaps = [artwork.audioUrls, artwork.audio];

  for (const candidate of languageOrder(lang)) {
    for (const map of audioMaps) {
      const url = readAudioFromMap(map, candidate);
      if (url) {
        return url;
      }
    }
  }

  if (isNonEmptyString(artwork.audioUrl)) {
    return artwork.audioUrl.trim();
  }

  return null;
}
